import React, { useState, useEffect } from 'react';
import { X, FolderOpen, Trash2, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  hasPasswordSet, setPassword, checkPassword,
  listRecents, deleteRecent,
} from './disciplinaryStorage';

const enconOrange = '#ee7203';

/**
 * Modal that lists recents from sessionStorage.
 * - First time used in a session, asks the user to set a password.
 * - On subsequent opens, asks for the password to decrypt.
 * - Items vanish when the browser tab closes.
 */
const DisciplinaryRecentsModal = ({ isOpen, onClose, onOpen }) => {
  const [stage, setStage] = useState('locked');   // 'locked' | 'unlocked' | 'set'
  const [pwInput, setPwInput] = useState('');
  const [pwInput2, setPwInput2] = useState('');
  const [unlockedPw, setUnlockedPw] = useState('');
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setPwInput(''); setPwInput2('');
    if (hasPasswordSet()) {
      setStage('locked');
    } else {
      setStage('set');
    }
  }, [isOpen]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [isOpen]);

  if (!isOpen) return null;

  const refresh = (pw) => {
    const list = listRecents(pw) || [];
    setItems(list);
  };

  const doSet = () => {
    if (pwInput.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    if (pwInput !== pwInput2) { toast.error('Passwords do not match'); return; }
    setPassword(pwInput);
    setUnlockedPw(pwInput);
    setStage('unlocked');
    refresh(pwInput);
  };

  const doUnlock = () => {
    if (!checkPassword(pwInput)) { toast.error('Incorrect password'); return; }
    setUnlockedPw(pwInput);
    setStage('unlocked');
    refresh(pwInput);
  };

  const doDelete = (id) => {
    if (!window.confirm('Delete this saved hearing script from your recents?')) return;
    deleteRecent(unlockedPw, id);
    refresh(unlockedPw);
    toast.success('Deleted');
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="disc-recents-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" style={{ border: `3px solid ${enconOrange}` }}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
          <FolderOpen className="w-5 h-5" style={{ color: enconOrange }} />
          <h3 className="text-lg font-extrabold text-gray-900">Recent Disciplinary Scripts</h3>
          <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
            this session only
          </span>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700" data-testid="disc-recents-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {stage === 'set' && (
            <div className="max-w-sm mx-auto text-center">
              <Lock className="w-10 h-10 mx-auto mb-3" style={{ color: enconOrange }} />
              <p className="text-sm text-gray-700 mb-1 font-semibold">Set a session password</p>
              <p className="text-xs text-gray-500 mb-4">
                Recents live only on this browser tab and are wiped when you close it. Choose a password — you'll re-enter it to view the list this session.
              </p>
              <input type="password" placeholder="Password" value={pwInput} onChange={(e) => setPwInput(e.target.value)} className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="disc-recents-pass1" />
              <input type="password" placeholder="Confirm password" value={pwInput2} onChange={(e) => setPwInput2(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSet()} className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="disc-recents-pass2" />
              <button onClick={doSet} className="w-full px-4 py-2 rounded-lg text-white font-semibold" style={{ backgroundColor: enconOrange }} data-testid="disc-recents-pass-set">
                Set password
              </button>
            </div>
          )}

          {stage === 'locked' && (
            <div className="max-w-sm mx-auto text-center">
              <Lock className="w-10 h-10 mx-auto mb-3" style={{ color: enconOrange }} />
              <p className="text-sm text-gray-700 mb-3 font-semibold">Enter your session password</p>
              <input type="password" placeholder="Password" value={pwInput} onChange={(e) => setPwInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doUnlock()} className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="disc-recents-pass" />
              <button onClick={doUnlock} className="w-full px-4 py-2 rounded-lg text-white font-semibold" style={{ backgroundColor: enconOrange }} data-testid="disc-recents-unlock">
                Unlock
              </button>
            </div>
          )}

          {stage === 'unlocked' && (
            items.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-10">
                No recent scripts saved this session.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {items.map((it) => {
                  const k = it.key_info || {};
                  return (
                    <li key={it.id} className="py-3 flex items-center gap-3 hover:bg-orange-50/50 rounded-lg px-2 transition-colors">
                      <button
                        type="button"
                        onClick={() => onOpen(it)}
                        className="flex-1 min-w-0 text-left"
                        data-testid={`disc-recent-open-${it.id}`}
                      >
                        <div className="font-semibold text-sm text-gray-900 truncate">{k.employee_name || '(unnamed)'}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {k.date || '—'} · {k.start_time || ''} · saved {new Date(it.saved_at || it.generated_at).toLocaleTimeString()}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => doDelete(it.id)}
                        className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded p-1.5 transition-colors"
                        title="Delete"
                        data-testid={`disc-recent-delete-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default DisciplinaryRecentsModal;
